import { z } from "zod";

// Material validation schema matching database constraints
export const materialSchema = z.object({
  name: z.string().min(1, "Name is required").max(200, "Name must be less than 200 characters"),
  name_cn: z.string().max(200).nullable().optional(),
  family: z.string().min(1, "Family is required").max(100),
  description: z.string().max(2000).nullable().optional(),
  source_paper: z.string().max(500).nullable().optional(),
  source_doi: z.string().max(200).nullable().optional(),
  source_url: z.string().url("Invalid URL format").max(500).nullable().optional().or(z.literal("")),
  paper_abstract: z.string().max(5000).nullable().optional(),
  degradation_value: z.string().max(200).nullable().optional(),
  degradation_source: z.string().max(500).nullable().optional(),
  degradation_method: z.string().max(500).nullable().optional(),
  tensile_strength_mpa: z.number().positive("Tensile strength must be positive").nullable().optional(),
  tensile_source: z.string().max(500).nullable().optional(),
  electrical_performance: z.string().max(500).nullable().optional(),
  electrical_source: z.string().max(500).nullable().optional(),
  water_resistance: z.string().max(200).nullable().optional(),
  water_resistance_source: z.string().max(500).nullable().optional(),
  cost_per_kg: z.number().positive("Cost must be positive").nullable().optional(),
  cost_source: z.string().max(500).nullable().optional(),
  applications_mentioned: z.array(z.string().max(200)).nullable().optional(),
  applications_source: z.string().max(500).nullable().optional(),
  fabrication_method: z.string().max(1000).nullable().optional(),
  fabrication_source: z.string().max(500).nullable().optional(),
  image_url: z.string().max(500).nullable().optional(),
  image_credit: z.string().max(500).nullable().optional(),
  image_source_url: z.string().url("Invalid URL format").max(500).nullable().optional().or(z.literal("")),
});

export type ValidatedMaterial = z.infer<typeof materialSchema>;

// Application validation schema matching database constraints
export const applicationSchema = z.object({
  title: z.string().min(1, "Title is required").max(200, "Title must be less than 200 characters"),
  title_cn: z.string().max(200).nullable().optional(),
  description: z.string().max(2000).nullable().optional(),
  category: z.string().max(100).nullable().optional(),
  application_type: z.enum(["consumer", "academic"]).nullable().optional(),
  image_url: z.string().max(500).nullable().optional(),
  image_credit: z.string().max(500).nullable().optional(),
  image_source_url: z.string().url("Invalid URL format").max(500).nullable().optional().or(z.literal("")),
  source_url: z.string().url("Invalid URL format").max(500).nullable().optional().or(z.literal("")),
  year: z.number().int().min(1900).max(2100).nullable().optional(),
  creators: z.array(z.string().max(100)).nullable().optional(),
  organization: z.string().max(200).nullable().optional(),
  technologies: z.array(z.string().max(100)).nullable().optional(),
  key_features: z.array(z.string().max(500)).nullable().optional(),
  technical_brief: z.string().max(2000).nullable().optional(),
  impact_brief: z.string().max(2000).nullable().optional(),
  trl_level: z.number().int().min(1).max(9).nullable().optional(),
  tags: z.array(z.string().max(50)).nullable().optional(),
});

export type ValidatedApplication = z.infer<typeof applicationSchema>;

// Helper function to sanitize and prepare data for validation
export function sanitizeRawData(rawData: Record<string, unknown>): Record<string, unknown> {
  const sanitized: Record<string, unknown> = {};
  
  for (const [key, value] of Object.entries(rawData)) {
    // Convert empty strings to null for optional fields
    if (value === "") {
      sanitized[key] = null;
    } 
    // Ensure string values are trimmed
    else if (typeof value === "string") {
      sanitized[key] = value.trim();
    }
    // Ensure arrays have trimmed string values
    else if (Array.isArray(value)) {
      sanitized[key] = value.map(item => 
        typeof item === "string" ? item.trim() : item
      ).filter(item => item !== "" && item != null);
    }
    else {
      sanitized[key] = value;
    }
  }
  
  return sanitized;
}

// Validate material data with detailed error messages
export function validateMaterial(rawData: Record<string, unknown>): { 
  success: true; data: ValidatedMaterial 
} | { 
  success: false; errors: string[] 
} {
  const sanitized = sanitizeRawData(rawData);
  const result = materialSchema.safeParse(sanitized);
  
  if (result.success) {
    return { success: true, data: result.data };
  }
  
  const errors = result.error.errors.map(err => 
    `${err.path.join(".")}: ${err.message}`
  );
  return { success: false, errors };
}

// Validate application data with detailed error messages
export function validateApplication(rawData: Record<string, unknown>): { 
  success: true; data: ValidatedApplication 
} | { 
  success: false; errors: string[] 
} {
  const sanitized = sanitizeRawData(rawData);
  const result = applicationSchema.safeParse(sanitized);
  
  if (result.success) {
    return { success: true, data: result.data };
  }
  
  const errors = result.error.errors.map(err => 
    `${err.path.join(".")}: ${err.message}`
  );
  return { success: false, errors };
}
